import React, { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigate, useParams } from 'react-router-dom';

import {
  LinkButton,
  CanvasContainer,
  ContentContainer,
  DicomLink,
  FooterContainer,
  FramesWrapper,
  TitleContainer,
} from './Canvas.style';
import Controls from './Controls';
import Frame from './Frame';
import KeypointerCanvas from './KeypointerCanvas';
import Scroller from './Scroller';
import SegmentationCanvas from './SegmentationCanvas';
import { isStagingEnv } from '../../../../utilities/env';
import ThreeDButton from '../ThreeDVisualizer/Button';
import DraggableThreeDViewer from '../ThreeDVisualizer/DraggableThreeDWrapper';

interface IProps {
  study: any;
  dicom: any;
  dicoms: any;
  changesAreSaved: any;
  frames: any;
  playVideo: any;
  pauseVideo: any;
  videoState: any;
  activeFrame: any;
  activeKeypoints: any;
  setFrames: any;
  setActiveFrame: any;
  setKeypoints: any;
  showMeasurements: any;
  phaseLabels: any;
  keypointerIsActive: any;
  segmentorIsActive: any;
  activeSegmentation: any;
  allSegmentations: any;
  allSegmentationPredictions: any;
  allSegmentationReferences: any;
  unfilteredSegmentationReferences: any;
  setActiveSegmentation: any;
  useOpenContours: any;
  showOtherSegmentations: any;
  showSegmentationPredictions: any;
  showSegmentationReferences: any;
  activeSegmentationClass: any;
  setSegmentationObjectToBeAdopted: any;
  editMode: boolean;
  activeSegmentationPredictionClasses: any;
  activeSegmentationReferenceClasses: any;
  activeOtherSegmentationClasses: any;
  setActiveOtherSegmentationClasses: any;
  pnoExistsResponse: any;
  rejectedFrameIndexes?: number[];
}

const Canvas: FC<IProps> = props => {
  const {
    study,
    dicom,
    dicoms,
    changesAreSaved,
    frames,
    playVideo,
    pauseVideo,
    videoState,
    activeFrame,
    activeKeypoints,
    setFrames,
    setActiveFrame,
    setKeypoints,
    showMeasurements,
    phaseLabels,
    keypointerIsActive,
    segmentorIsActive,
    activeSegmentation,
    allSegmentations,
    allSegmentationPredictions,
    allSegmentationReferences,
    unfilteredSegmentationReferences,
    setActiveSegmentation,
    useOpenContours,
    showOtherSegmentations,
    showSegmentationPredictions,
    showSegmentationReferences,
    activeSegmentationClass,
    setSegmentationObjectToBeAdopted,
    editMode,
    activeSegmentationPredictionClasses,
    activeSegmentationReferenceClasses,
    activeOtherSegmentationClasses,
    pnoExistsResponse,
    rejectedFrameIndexes,
  } = props;
  const navigate = useNavigate();

  const { studyIcid, dicomIcid } = useParams();

  const [scale, setScale] = useState(1);
  const [inFrame, setInFrame] = useState(false);
  const [show3D, setShow3D] = useState(false);

  const numberOfFrames = frames?.length;
  const activeDicomIndex = dicoms?.findIndex(dicom => dicom.icid === dicomIcid);
  const previousDicom = dicoms?.[activeDicomIndex - 1];
  const nextDicom = dicoms?.[activeDicomIndex + 1];

  const previousButtonIsEnabled = !!previousDicom;
  const nextButtonIsEnabled = !!nextDicom;

  const previousDicomLink = `/${studyIcid}/${previousDicom?.icid}`;
  const nextDicomLink = `/${studyIcid}/${nextDicom?.icid}`;

  const canvasFrames = useMemo(() => {
    return (frames ?? [])
      .slice()
      .sort((a, b) => a?.index - b?.index)
      .map(frame => {
        const isActive = frame?.index === activeFrame?.index;
        return (
          <Frame
            key={`frame-${frame.index}`}
            dicom={dicom}
            frame={frame}
            isActive={isActive}
            scale={scale}
          />
        );
      });
  }, [activeFrame?.index, dicom, frames, scale, setFrames]);

  const isStaging = isStagingEnv();

  const dicomLink = (
    <DicomLink
      href={`${
        isStaging
          ? `https://staging.atlas.icardio.ai?dicom=${dicom?.icid}`
          : `https://atlas.icardio.ai?dicom=${dicom?.icid}`
      }`}
      target="_blank"
    >
      {dicom?.icid}
    </DicomLink>
  );

  useEffect(() => {
    const handleScroll = event => {
      if (!inFrame) return;

      event?.preventDefault();
      event?.stopPropagation();

      const direction = event.deltaY;
      // Adjust these values based on how fast you want the zoom to happen
      const zoomIntensity = 0.1;
      let newScale = scale;

      if (direction < 0) {
        // Scroll up, zoom in
        newScale += zoomIntensity;
        if (newScale > 2) newScale = 2; // Prevent zooming in beyond 10x (arbitrary value, adjust as needed
      } else {
        // Scroll down, zoom out
        newScale -= zoomIntensity;
        if (newScale < 1) newScale = 1; // Prevent zooming out beyond the original size
      }

      setScale(newScale);
    };

    window.addEventListener('wheel', handleScroll, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleScroll);
    };
  }, [scale, inFrame]);

  const handlePrev = event => {
    event.preventDefault();
    if (!previousButtonIsEnabled) return;
    if (!changesAreSaved && !window.confirm('You have unsaved changes on this dicom, continue?'))
      return;
    navigate(previousDicomLink);
  };

  const handleNext = event => {
    event.preventDefault();
    if (!nextButtonIsEnabled) return;
    if (!changesAreSaved && !window.confirm('You have unsaved changes on this dicom, continue?'))
      return;
    navigate(nextDicomLink);
  };

  useHotkeys('F', event => {
    event?.preventDefault();
    handlePrev(event);
  });

  useHotkeys('H', event => {
    event?.preventDefault();
    handleNext(event);
  });

  return (
    <CanvasContainer>
      <TitleContainer>
        <LinkButton
          disabled={!previousButtonIsEnabled}
          onClick={handlePrev}
          href={previousDicomLink}
        >
          (F){' '}
          {previousButtonIsEnabled ? `[${String(previousDicom?.index + 1).padStart(2, '0')}]` : ''}{' '}
          PREVIOUS
        </LinkButton>
        <div>
          [{String(dicom?.index + 1).padStart(2, '0')}/{study?.numberOfDicoms}] {dicomLink} - [
          {String(activeFrame?.index + 1).padStart(2, '0')}/{frames?.length}] {activeFrame?.icid}
        </div>
        <LinkButton disabled={!nextButtonIsEnabled} onClick={handleNext} href={nextDicomLink}>
          (H) {nextButtonIsEnabled ? `[${String(nextDicom?.index + 1).padStart(2, '0')}]` : ''} NEXT
        </LinkButton>
      </TitleContainer>
      <ContentContainer
        onMouseEnter={() => setInFrame(true)}
        onMouseLeave={() => setInFrame(false)}
      >
        <FramesWrapper>{canvasFrames}</FramesWrapper>
        {keypointerIsActive && (
          <KeypointerCanvas
            activeFrame={activeFrame?.index}
            activeKeypoints={activeKeypoints}
            dicom={dicom}
            scale={scale}
            setKeypoints={setKeypoints}
            showMeasurements={showMeasurements}
          />
        )}
        {segmentorIsActive && (
          <SegmentationCanvas
            activeFrameIndex={activeFrame?.index}
            activeSegmentation={activeSegmentation}
            allSegmentations={allSegmentations}
            allSegmentationPredictions={allSegmentationPredictions}
            allSegmentationReferences={allSegmentationReferences}
            dicom={dicom}
            scale={scale}
            setActiveSegmentation={setActiveSegmentation}
            useOpenContours={useOpenContours}
            showOtherSegmentations={showOtherSegmentations}
            showSegmentationPredictions={showSegmentationPredictions}
            showSegmentationReferences={showSegmentationReferences}
            activeSegmentationClass={activeSegmentationClass}
            setSegmentationObjectToBeAdopted={setSegmentationObjectToBeAdopted}
            editMode={editMode}
            activeSegmentationPredictionClasses={activeSegmentationPredictionClasses}
            activeSegmentationReferenceClasses={activeSegmentationReferenceClasses}
            activeOtherSegmentationClasses={activeOtherSegmentationClasses}
          />
        )}
        {dicom?.type === 'ICE Standard' && pnoExistsResponse?.pnoExists && (
          <>
            <ThreeDButton onClick={() => setShow3D(!show3D)} active={show3D} />
            {show3D && (
              <DraggableThreeDViewer
                onClose={() => setShow3D(false)}
                studyIcid={studyIcid}
                dicomIcid={dicomIcid}
                activeFrame={activeFrame}
                imageHeight={dicom.pixelHeight}
                imageWidth={dicom.pixelWidth}
              />
            )}
          </>
        )}
      </ContentContainer>
      <FooterContainer>
        {numberOfFrames > 1 ? (
          <>
            <Scroller
              pauseVideo={pauseVideo}
              frames={frames}
              activeFrame={activeFrame?.index}
              setActiveFrame={setActiveFrame}
              phaseLabels={phaseLabels}
              segmentationReferences={unfilteredSegmentationReferences}
              rejectedFrameIndexes={rejectedFrameIndexes}
            />
            <Controls
              playVideo={playVideo}
              pauseVideo={pauseVideo}
              videoState={videoState}
              setActiveFrame={setActiveFrame}
              numberOfFrames={numberOfFrames}
            />
          </>
        ) : (
          <div style={{ height: 85 }}></div>
        )}
      </FooterContainer>
    </CanvasContainer>
  );
};

export default Canvas;
